import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { tournamentsService, matchesService } from '../../services/supabaseService';
import Header from '../../components/navigation/Header';
import Breadcrumb from '../../components/navigation/Breadcrumb';
import Icon from '../../components/AppIcon';
import DrillDownPanel from './components/DrillDownPanel';

// ─── helpers ────────────────────────────────────────────────────────────────

function toCamel(str) {
  return str?.replace(/_([a-z])/g, (_, l) => l?.toUpperCase());
}

function objToCamel(obj) {
  if (Array.isArray(obj)) return obj?.map(objToCamel);
  if (obj && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.keys(obj)?.reduce((acc, k) => {
      acc[toCamel(k)] = objToCamel(obj?.[k]);
      return acc;
    }, {});
  }
  return obj;
}

const CATEGORY_DEFS = [
  { key: 'matchWinner',        settingKey: 'matchWinner',        label: 'Winner',    icon: 'Trophy',      predField: 'winnerId',            matchField: 'winnerId',            defaultPts: 10 },
  { key: 'tossWinner',         settingKey: 'tossWinner',         label: 'Toss',      icon: 'Coins',       predField: 'tossWinnerId',         matchField: 'tossWinnerId',        defaultPts: 5  },
  { key: 'battingFirst',       settingKey: 'battingFirst',       label: 'Bat First', icon: 'ArrowUpCircle', predField: 'battingFirstId',     matchField: 'battingFirstId',      defaultPts: 5  },
  { key: 'manOfMatch',         settingKey: 'manOfMatch',         label: 'MoM',       icon: 'Star',        predField: 'manOfMatchId',         matchField: 'manOfMatchId',        defaultPts: 15 },
  { key: 'highestRunScorer',   settingKey: 'highestRunScorer',   label: 'Top Scorer',icon: 'TrendingUp',  predField: 'highestScorerId',      matchField: 'highestScorerId',     defaultPts: 20 },
  { key: 'highestWicketTaker', settingKey: 'highestWicketTaker', label: 'Top Wicket',icon: 'Zap',         predField: 'highestWicketTakerId', matchField: 'highestWicketTakerId',defaultPts: 20 },
];

const CONF_KEYS = {
  matchWinner: 'winnerConfidence',
  manOfMatch:  'motmConfidence',
  highestRunScorer: 'scorerConfidence',
  highestWicketTaker: 'wicketConfidence',
};

function calcPoints(pred, matchData, matchSettings, bonusSettings) {
  let pts = 0;
  let confBonus = 0;
  let correct = 0;
  let total = 0;
  const breakdown = {};

  for (const cat of CATEGORY_DEFS) {
    const setting = matchSettings?.[cat?.settingKey];
    if (!setting?.enabled) continue;
    const predVal = pred?.[cat?.predField];
    const actualVal = matchData?.[cat?.matchField];
    if (!predVal || !actualVal) continue;
    total++;
    const isCorrect = predVal === actualVal;
    let catPts = 0;
    let catConf = 0;
    if (isCorrect) {
      catPts = setting?.points || cat?.defaultPts;
      pts += catPts;
      correct++;
      const confField = CONF_KEYS?.[cat?.key];
      if (confField && bonusSettings?.confidenceBonus?.enabled && pred?.[confField]) {
        const cv = pred?.[confField];
        if (cv >= 4) catConf = bonusSettings?.confidenceBonus?.highConfidence || 10;
        else if (cv >= 2) catConf = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
        confBonus += catConf;
      }
    }
    breakdown[cat.key] = { points: catPts + catConf, isCorrect, hasPrediction: true, hasActual: true };
  }

  return { pts, confBonus, total: pts + confBonus, correct, totalPredictions: total, breakdown };
}

// ─── component ──────────────────────────────────────────────────────────────

const PointsVerification = () => {
  const [activeView, setActiveView] = useState('byMatch'); // 'byMatch' | 'byParticipant'

  // ── By Match view state ──
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState('');
  const [matches, setMatches] = useState([]);
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [gridData, setGridData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [drillDown, setDrillDown] = useState(null);

  // ── By Participant view state ──
  const [participants, setParticipants] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [participantData, setParticipantData] = useState(null); // { matchRows, settings }
  const [participantLoading, setParticipantLoading] = useState(false);
  const [participantsLoading, setParticipantsLoading] = useState(false);

  // Load tournaments
  useEffect(() => {
    const load = async () => {
      try {
        setTournamentsLoading(true);
        const data = await tournamentsService?.getAll();
        setTournaments(data || []);
        if (data?.length > 0) setSelectedTournamentId(data?.[0]?.id);
      } catch (e) {
        console.error('Error loading tournaments:', e);
      } finally {
        setTournamentsLoading(false);
      }
    };
    load();
  }, []);

  // Load matches when tournament changes
  useEffect(() => {
    if (!selectedTournamentId) return;
    const load = async () => {
      try {
        setMatchesLoading(true);
        setSelectedMatchId('');
        setGridData(null);
        const data = await matchesService?.getByTournament(selectedTournamentId);
        const published = (data || [])?.filter(m => {
          const lockTime = m?.matchLockTime ? new Date(m?.matchLockTime) : null;
          const isLocked = lockTime ? new Date() >= lockTime : false;
          return (m?.resultsStatus === 'published' || m?.matchStatus === 'live' || isLocked);
        });
        setMatches(published);
        if (published?.length > 0) setSelectedMatchId(published?.[published?.length - 1]?.id);
      } catch (e) {
        console.error('Error loading matches:', e);
      } finally {
        setMatchesLoading(false);
      }
    };
    load();
  }, [selectedTournamentId]);

  // Load participants when tournament changes (for By Participant view)
  useEffect(() => {
    if (!selectedTournamentId) return;
    const load = async () => {
      try {
        setParticipantsLoading(true);
        setSelectedUserId('');
        setParticipantData(null);
        const { data: participantsRaw } = await supabase
          ?.from('tournament_participants')
          ?.select('user_id, user_profiles!inner(id, full_name, email)')
          ?.eq('tournament_id', selectedTournamentId);
        const parts = objToCamel(participantsRaw || []);
        setParticipants(parts);
        if (parts?.length > 0) {
          const firstId = parts?.[0]?.userId || parts?.[0]?.userProfiles?.id;
          setSelectedUserId(firstId);
        }
      } catch (e) {
        console.error('Error loading participants:', e);
      } finally {
        setParticipantsLoading(false);
      }
    };
    load();
  }, [selectedTournamentId]);

  // Load grid data when match changes (By Match view)
  const loadGrid = useCallback(async () => {
    if (!selectedMatchId || !selectedTournamentId) return;
    try {
      setLoading(true);
      setGridData(null);

      const { data: matchRaw, error: matchErr } = await supabase?.from('matches')?.select(`
          *,
          team1:teams!matches_team1_id_fkey(id, name, short_name),
          team2:teams!matches_team2_id_fkey(id, name, short_name),
          winner_team:teams!matches_winner_id_fkey(id, name, short_name),
          toss_team:teams!matches_toss_winner_id_fkey(id, name, short_name),
          batting_team:teams!matches_batting_first_id_fkey(id, name, short_name)
        `)?.eq('id', selectedMatchId)?.single();

      if (matchErr) throw matchErr;
      const matchData = objToCamel(matchRaw);

      const { data: tournRaw } = await supabase?.from('tournaments')?.select('prediction_settings')?.eq('id', selectedTournamentId)?.single();
      const settings = tournRaw?.prediction_settings || {};

      const { data: predsRaw, error: predsErr } = await supabase?.from('match_predictions')?.select(`
          *,
          user_profiles!inner(id, full_name, email)
        `)?.eq('match_id', selectedMatchId);

      if (predsErr) throw predsErr;
      const predictions = objToCamel(predsRaw || []);

      const { data: participantsRaw } = await supabase?.from('tournament_participants')?.select('user_id, user_profiles!inner(id, full_name, email)')?.eq('tournament_id', selectedTournamentId);
      const allParticipants = objToCamel(participantsRaw || []);

      const playerIds = [
        matchData?.manOfMatchId,
        matchData?.highestScorerId,
        matchData?.highestWicketTakerId,
        ...predictions?.map(p => p?.manOfMatchId)?.filter(Boolean),
        ...predictions?.map(p => p?.highestScorerId)?.filter(Boolean),
        ...predictions?.map(p => p?.highestWicketTakerId)?.filter(Boolean),
      ]?.filter(Boolean);

      let playerMap = {};
      if (playerIds?.length > 0) {
        const { data: playersRaw } = await supabase?.from('players')?.select('id, name')?.in('id', [...new Set(playerIds)]);
        (playersRaw || [])?.forEach(p => { playerMap[p.id] = p?.name; });
      }

      const enrichedMatch = {
        ...matchData,
        team1Name: matchData?.team1?.shortName || matchData?.team1?.name || 'Team 1',
        team2Name: matchData?.team2?.shortName || matchData?.team2?.name || 'Team 2',
        winnerName: matchData?.winnerTeam?.shortName || matchData?.winnerTeam?.name || '—',
        tossWinnerName: matchData?.tossTeam?.shortName || matchData?.tossTeam?.name || '—',
        battingFirstName: matchData?.battingTeam?.shortName || matchData?.battingTeam?.name || '—',
        manOfMatchName: playerMap?.[matchData?.manOfMatchId] || '—',
        highestScorerName: playerMap?.[matchData?.highestScorerId] || '—',
        highestWicketTakerName: playerMap?.[matchData?.highestWicketTakerId] || '—',
      };

      const predByUser = {};
      predictions?.forEach(p => { predByUser[p.userId] = p; });

      const teamMap = {};
      [matchData?.team1, matchData?.team2]?.forEach(t => { if (t?.id) teamMap[t.id] = t?.shortName || t?.name; });

      const users = allParticipants?.map(part => {
        const userId = part?.userId || part?.userProfiles?.id;
        const profile = part?.userProfiles;
        const pred = predByUser?.[userId];
        const matchSettings = settings?.matchLevel || {};
        const bonusSettings = settings?.bonusPoints || {};

        const result = pred
          ? calcPoints(pred, enrichedMatch, matchSettings, bonusSettings)
          : { pts: 0, confBonus: 0, total: 0, correct: 0, totalPredictions: 0, breakdown: {} };

        const enrichedPred = pred ? {
          ...pred,
          winnerName: teamMap?.[pred?.winnerId] || pred?.winnerId || '—',
          tossWinnerName: teamMap?.[pred?.tossWinnerId] || pred?.tossWinnerId || '—',
          battingFirstName: teamMap?.[pred?.battingFirstId] || pred?.battingFirstId || '—',
          manOfMatchName: playerMap?.[pred?.manOfMatchId] || pred?.manOfMatchId || '—',
          highestScorerName: playerMap?.[pred?.highestScorerId] || pred?.highestScorerId || '—',
          highestWicketTakerName: playerMap?.[pred?.highestWicketTakerId] || pred?.highestWicketTakerId || '—',
        } : null;

        return {
          userId,
          name: profile?.fullName || profile?.email || 'Unknown',
          email: profile?.email,
          hasPrediction: !!pred,
          prediction: enrichedPred,
          ...result,
        };
      });

      users?.sort((a, b) => b?.total - a?.total);

      setGridData({ users, matchData: enrichedMatch, settings });
    } catch (e) {
      console.error('Error loading grid:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedMatchId, selectedTournamentId]);

  useEffect(() => {
    loadGrid();
  }, [loadGrid]);

  // Load participant match-wise data (By Participant view)
  const loadParticipantData = useCallback(async () => {
    if (!selectedUserId || !selectedTournamentId || matches?.length === 0) return;
    try {
      setParticipantLoading(true);
      setParticipantData(null);

      // Get tournament settings
      const { data: tournRaw } = await supabase?.from('tournaments')?.select('prediction_settings')?.eq('id', selectedTournamentId)?.single();
      const settings = tournRaw?.prediction_settings || {};

      // Get all predictions for this user in this tournament's matches
      const matchIds = matches?.map(m => m?.id);
      const { data: predsRaw } = await supabase
        ?.from('match_predictions')
        ?.select('*')
        ?.eq('user_id', selectedUserId)
        ?.in('match_id', matchIds);
      const predictions = objToCamel(predsRaw || []);
      const predByMatch = {};
      predictions?.forEach(p => { predByMatch[p.matchId] = p; });

      // Fetch all matches with team info
      const { data: matchesRaw } = await supabase
        ?.from('matches')
        ?.select(`
          *,
          team1:teams!matches_team1_id_fkey(id, name, short_name),
          team2:teams!matches_team2_id_fkey(id, name, short_name),
          winner_team:teams!matches_winner_id_fkey(id, name, short_name),
          toss_team:teams!matches_toss_winner_id_fkey(id, name, short_name),
          batting_team:teams!matches_batting_first_id_fkey(id, name, short_name)
        `)
        ?.in('id', matchIds);
      const matchesData = objToCamel(matchesRaw || []);

      // Collect all player IDs needed
      const playerIds = [];
      matchesData?.forEach(m => {
        if (m?.manOfMatchId) playerIds?.push(m?.manOfMatchId);
        if (m?.highestScorerId) playerIds?.push(m?.highestScorerId);
        if (m?.highestWicketTakerId) playerIds?.push(m?.highestWicketTakerId);
      });
      predictions?.forEach(p => {
        if (p?.manOfMatchId) playerIds?.push(p?.manOfMatchId);
        if (p?.highestScorerId) playerIds?.push(p?.highestScorerId);
        if (p?.highestWicketTakerId) playerIds?.push(p?.highestWicketTakerId);
      });

      let playerMap = {};
      if (playerIds?.length > 0) {
        const { data: playersRaw } = await supabase?.from('players')?.select('id, name')?.in('id', [...new Set(playerIds)]);
        (playersRaw || [])?.forEach(p => { playerMap[p.id] = p?.name; });
      }

      // Build match rows
      const matchRows = matchesData?.map(matchRaw => {
        const teamMap = {};
        [matchRaw?.team1, matchRaw?.team2]?.forEach(t => { if (t?.id) teamMap[t.id] = t?.shortName || t?.name; });

        const enrichedMatch = {
          ...matchRaw,
          team1Name: matchRaw?.team1?.shortName || matchRaw?.team1?.name || 'Team 1',
          team2Name: matchRaw?.team2?.shortName || matchRaw?.team2?.name || 'Team 2',
          winnerName: matchRaw?.winnerTeam?.shortName || matchRaw?.winnerTeam?.name || '—',
          tossWinnerName: matchRaw?.tossTeam?.shortName || matchRaw?.tossTeam?.name || '—',
          battingFirstName: matchRaw?.battingTeam?.shortName || matchRaw?.battingTeam?.name || '—',
          manOfMatchName: playerMap?.[matchRaw?.manOfMatchId] || '—',
          highestScorerName: playerMap?.[matchRaw?.highestScorerId] || '—',
          highestWicketTakerName: playerMap?.[matchRaw?.highestWicketTakerId] || '—',
        };

        const pred = predByMatch?.[matchRaw?.id];
        const matchSettings = settings?.matchLevel || {};
        const bonusSettings = settings?.bonusPoints || {};

        const result = pred
          ? calcPoints(pred, enrichedMatch, matchSettings, bonusSettings)
          : { pts: 0, confBonus: 0, total: 0, correct: 0, totalPredictions: 0, breakdown: {} };

        const enrichedPred = pred ? {
          ...pred,
          winnerName: teamMap?.[pred?.winnerId] || pred?.winnerId || '—',
          tossWinnerName: teamMap?.[pred?.tossWinnerId] || pred?.tossWinnerId || '—',
          battingFirstName: teamMap?.[pred?.battingFirstId] || pred?.battingFirstId || '—',
          manOfMatchName: playerMap?.[pred?.manOfMatchId] || pred?.manOfMatchId || '—',
          highestScorerName: playerMap?.[pred?.highestScorerId] || pred?.highestScorerId || '—',
          highestWicketTakerName: playerMap?.[pred?.highestWicketTakerId] || pred?.highestWicketTakerId || '—',
        } : null;

        return {
          matchId: matchRaw?.id,
          matchLabel: `${enrichedMatch?.team1Name} vs ${enrichedMatch?.team2Name}`,
          matchDate: matchRaw?.matchDate,
          matchData: enrichedMatch,
          hasPrediction: !!pred,
          prediction: enrichedPred,
          settings,
          ...result,
        };
      });

      // Sort by match date ascending
      matchRows?.sort((a, b) => {
        if (!a?.matchDate) return 1;
        if (!b?.matchDate) return -1;
        return new Date(a.matchDate) - new Date(b.matchDate);
      });

      setParticipantData({ matchRows, settings });
    } catch (e) {
      console.error('Error loading participant data:', e);
    } finally {
      setParticipantLoading(false);
    }
  }, [selectedUserId, selectedTournamentId, matches]);

  useEffect(() => {
    if (activeView === 'byParticipant') {
      loadParticipantData();
    }
  }, [activeView, loadParticipantData]);

  const enabledCategories = gridData
    ? CATEGORY_DEFS?.filter(c => gridData?.settings?.matchLevel?.[c?.settingKey]?.enabled)
    : [];

  // For participant view, derive enabled categories from participantData settings
  const participantEnabledCategories = participantData
    ? CATEGORY_DEFS?.filter(c => participantData?.settings?.matchLevel?.[c?.settingKey]?.enabled)
    : [];

  const selectedTournament = tournaments?.find(t => t?.id === selectedTournamentId);
  const selectedParticipant = participants?.find(p => (p?.userId || p?.userProfiles?.id) === selectedUserId);
  const selectedParticipantName = selectedParticipant?.userProfiles?.fullName || selectedParticipant?.userProfiles?.email || 'Unknown';

  const breadcrumbItems = [
    { label: 'Admin Dashboard', path: '/admin-dashboard' },
    { label: 'Points Verification' }
  ];

  const handleDrillDown = (user) => {
    if (!user?.hasPrediction) return;
    setDrillDown({
      user,
      match: gridData?.matchData,
      prediction: user?.prediction,
      settings: gridData?.settings,
    });
  };

  // Participant view totals
  const participantTotals = participantData?.matchRows?.reduce(
    (acc, row) => ({
      total: acc?.total + (row?.total || 0),
      correct: acc?.correct + (row?.correct || 0),
      totalPredictions: acc?.totalPredictions + (row?.totalPredictions || 0),
    }),
    { total: 0, correct: 0, totalPredictions: 0 }
  ) || { total: 0, correct: 0, totalPredictions: 0 };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} />

        {/* Page title */}
        <div className="mt-4 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <Icon name="ShieldCheck" size={20} className="text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Points Verification</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Verify points calculations per match across all participants</p>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 mb-6 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-fit">
          <button
            onClick={() => setActiveView('byMatch')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeView === 'byMatch' ?'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' :'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Icon name="LayoutGrid" size={15} />
            By Match
          </button>
          <button
            onClick={() => setActiveView('byParticipant')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeView === 'byParticipant' ?'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' :'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Icon name="User" size={15} />
            By Participant
          </button>
        </div>

        {/* Tournament Selector (shared) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">Tournament</label>
            {tournamentsLoading ? (
              <div className="h-10 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
            ) : (
              <select
                value={selectedTournamentId}
                onChange={e => setSelectedTournamentId(e?.target?.value)}
                className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {tournaments?.map(t => (
                  <option key={t?.id} value={t?.id}>{t?.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Match selector (By Match view only) */}
          {activeView === 'byMatch' && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                Match <span className="text-gray-400 font-normal normal-case">(published results only)</span>
              </label>
              {matchesLoading ? (
                <div className="h-10 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
              ) : matches?.length === 0 ? (
                <div className="h-10 flex items-center px-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-sm text-gray-400">
                  No published matches in this tournament
                </div>
              ) : (
                <select
                  value={selectedMatchId}
                  onChange={e => setSelectedMatchId(e?.target?.value)}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {matches?.map(m => (
                    <option key={m?.id} value={m?.id}>
                      {m?.team1?.shortName || m?.team1?.name || 'Team 1'} vs {m?.team2?.shortName || m?.team2?.name || 'Team 2'}
                      {m?.matchDate ? ` — ${new Date(m.matchDate)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Participant selector (By Participant view only) */}
          {activeView === 'byParticipant' && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1.5">Participant</label>
              {participantsLoading ? (
                <div className="h-10 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
              ) : participants?.length === 0 ? (
                <div className="h-10 flex items-center px-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 text-sm text-gray-400">
                  No participants in this tournament
                </div>
              ) : (
                <select
                  value={selectedUserId}
                  onChange={e => {
                    setSelectedUserId(e?.target?.value);
                  }}
                  className="w-full h-10 px-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {participants?.map(p => {
                    const uid = p?.userId || p?.userProfiles?.id;
                    const name = p?.userProfiles?.fullName || p?.userProfiles?.email || 'Unknown';
                    return (
                      <option key={uid} value={uid}>{name}</option>
                    );
                  })}
                </select>
              )}
            </div>
          )}
        </div>

        {/* ── BY MATCH VIEW ── */}
        {activeView === 'byMatch' && (
          <>
            {/* Match result summary */}
            {gridData?.matchData && !loading && (
              <div className="mb-5 p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Match Result</p>
                <div className="flex flex-wrap gap-4">
                  {[
                    { label: 'Winner', value: gridData?.matchData?.winnerName, icon: 'Trophy', color: 'text-amber-600 dark:text-amber-400' },
                    { label: 'Toss Winner', value: gridData?.matchData?.tossWinnerName, icon: 'Coins', color: 'text-blue-600 dark:text-blue-400' },
                    { label: 'Batted First', value: gridData?.matchData?.battingFirstName, icon: 'ArrowUpCircle', color: 'text-indigo-600 dark:text-indigo-400' },
                    { label: 'Man of Match', value: gridData?.matchData?.manOfMatchName, icon: 'Star', color: 'text-purple-600 dark:text-purple-400' },
                    { label: 'Top Scorer', value: gridData?.matchData?.highestScorerName, icon: 'TrendingUp', color: 'text-emerald-600 dark:text-emerald-400' },
                    { label: 'Top Wicket', value: gridData?.matchData?.highestWicketTakerName, icon: 'Zap', color: 'text-rose-600 dark:text-rose-400' },
                  ]?.filter(item => item?.value && item?.value !== '—')?.map(item => (
                    <div key={item?.label} className="flex items-center gap-2 min-w-[120px]">
                      <Icon name={item?.icon} size={14} className={item?.color} />
                      <div>
                        <p className="text-xs text-gray-400">{item?.label}</p>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{item?.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Grid */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading points data…</p>
              </div>
            ) : !selectedMatchId || matches?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <Icon name="BarChart2" size={24} className="text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Select a tournament and match to view points</p>
              </div>
            ) : gridData ? (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Icon name="Users" size={16} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {gridData?.users?.length} participant{gridData?.users?.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/80">
                      <tr>
                        <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/80 text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[120px]">
                          Participant
                        </th>
                        {enabledCategories?.map(cat => (
                          <th key={cat?.key} className={`text-center px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider ${cat?.key === 'manOfMatch' ? 'min-w-[130px]' : 'min-w-[80px]'}`}>
                            <div className="flex flex-col items-center gap-1">
                              <Icon name={cat?.icon} size={13} />
                              <span>{cat?.label}</span>
                            </div>
                          </th>
                        ))}
                        <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[55px]">
                          Correct
                        </th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[80px]">
                          Total Pts
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {gridData?.users?.map((user) => (
                        <tr
                          key={user?.userId}
                          className={`transition-colors ${user?.hasPrediction ? 'opacity-60' : 'opacity-100'}`}
                        >
                          <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-5 py-3.5">
                            <div className="flex items-center gap-2.5">
                              <div className="min-w-0">
                                <p className="font-medium text-gray-800 dark:text-gray-200 truncate max-w-[120px]">{user?.name}</p>
                                {!user?.hasPrediction && (
                                  <p className="text-xs text-gray-400 italic">No prediction</p>
                                )}
                              </div>
                            </div>
                          </td>

                          {enabledCategories?.map(cat => {
                            const bd = user?.breakdown?.[cat?.key];
                            const pred = user?.prediction;
                            const predictedNameMap = {
                              matchWinner: pred?.winnerName,
                              tossWinner: pred?.tossWinnerName,
                              battingFirst: pred?.battingFirstName,
                              manOfMatch: pred?.manOfMatchName,
                              highestRunScorer: pred?.highestScorerName,
                              highestWicketTaker: pred?.highestWicketTakerName,
                            };
                            const predictedName = predictedNameMap?.[cat?.key];
                            const isResultPublished = gridData?.matchData?.resultsStatus === 'published';

                            if (!user?.hasPrediction || !pred) {
                              return (
                                <td key={cat?.key} className="text-center px-3 py-3.5">
                                  <span className="text-gray-300 dark:text-gray-600">—</span>
                                </td>
                              );
                            }
                            return (
                              <td key={cat?.key} className="text-center px-3 py-3.5">
                                <div className="flex flex-col items-center gap-0.5">
                                  <span className={`text-xs font-medium truncate ${cat?.key === 'manOfMatch' ? 'max-w-[120px]' : 'max-w-[90px]'} ${bd?.isCorrect ? 'text-emerald-700 dark:text-emerald-400' : bd?.hasPrediction && bd?.hasActual ? 'text-red-500 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>
                                    {predictedName && predictedName !== '—' ? predictedName : <span className="text-gray-400 italic">—</span>}
                                  </span>
                                  {isResultPublished && bd && (
                                    bd?.isCorrect ? (
                                      <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold leading-none">
                                        +{bd?.points}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-900/20 text-red-400 dark:text-red-500 text-xs leading-none">
                                        0
                                      </span>
                                    )
                                  )}
                                </div>
                              </td>
                            );
                          })}

                          <td className="text-center px-3 py-3.5">
                            {user?.hasPrediction ? (
                              <span className={`text-sm font-semibold ${user?.correct > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                                {user?.correct}/{user?.totalPredictions}
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600">—</span>
                            )}
                          </td>

                          <td className="text-right px-5 py-3.5">
                            <span className={`text-base font-bold ${user?.total > 0 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`}>
                              {user?.total}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-emerald-100 dark:bg-emerald-900/40 inline-block" />
                    Correct prediction (points awarded)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-red-50 dark:bg-red-900/20 inline-block" />
                    Wrong prediction (0 points)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-gray-100 dark:bg-gray-700 inline-block" />
                    No prediction / not applicable
                  </span>
                </div>
              </div>
            ) : null}
          </>
        )}

        {/* ── BY PARTICIPANT VIEW ── */}
        {activeView === 'byParticipant' && (
          <>
            {participantLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading participant data…</p>
              </div>
            ) : !selectedUserId || participants?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <Icon name="User" size={24} className="text-gray-400" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Select a tournament and participant to view match-wise points</p>
              </div>
            ) : participantData ? (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                {/* Header with participant summary */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={16} className="text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm">{selectedParticipantName}</p>
                      <p className="text-xs text-gray-400">{participantData?.matchRows?.length} match{participantData?.matchRows?.length !== 1 ? 'es' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-5">
                    <div className="text-center">
                      <p className="text-xs text-gray-400 mb-0.5">Total Points</p>
                      <p className="text-lg font-bold text-primary-600 dark:text-primary-400">{participantTotals?.total}</p>
                    </div>
                    <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
                    <div className="text-center">
                      <p className="text-xs text-gray-400 mb-0.5">Correct</p>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                        {participantTotals?.correct}/{participantTotals?.totalPredictions}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/80">
                      <tr>
                        <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/80 text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[160px]">
                          Match
                        </th>
                        {participantEnabledCategories?.map(cat => (
                          <th key={cat?.key} className={`text-center px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider ${cat?.key === 'manOfMatch' || cat?.key === 'highestRunScorer' || cat?.key === 'highestWicketTaker' ? 'min-w-[140px]' : 'min-w-[100px]'}`}>
                            <div className="flex flex-col items-center gap-1">
                              <Icon name={cat?.icon} size={13} />
                              <span>{cat?.label}</span>
                            </div>
                          </th>
                        ))}
                        <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[60px]">
                          Correct
                        </th>
                        <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[80px]">
                          Points
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                      {participantData?.matchRows?.map((row) => {
                        const isResultPublished = row?.matchData?.resultsStatus === 'published';
                        return (
                          <tr key={row?.matchId} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition-colors">
                            {/* Match column */}
                            <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-5 py-3.5">
                              <div>
                                <p className="font-medium text-gray-800 dark:text-gray-200 text-xs leading-tight">{row?.matchLabel}</p>
                                {row?.matchDate && (
                                  <p className="text-xs text-gray-400 mt-0.5">
                                    {new Date(row.matchDate)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                  </p>
                                )}
                                {!row?.hasPrediction && (
                                  <p className="text-xs text-gray-400 italic mt-0.5">No prediction</p>
                                )}
                              </div>
                            </td>

                            {/* Category cells */}
                            {participantEnabledCategories?.map(cat => {
                              const bd = row?.breakdown?.[cat?.key];
                              const pred = row?.prediction;

                              // Actual result names
                              const actualNameMap = {
                                matchWinner: row?.matchData?.winnerName,
                                tossWinner: row?.matchData?.tossWinnerName,
                                battingFirst: row?.matchData?.battingFirstName,
                                manOfMatch: row?.matchData?.manOfMatchName,
                                highestRunScorer: row?.matchData?.highestScorerName,
                                highestWicketTaker: row?.matchData?.highestWicketTakerName,
                              };
                              const predictedNameMap = {
                                matchWinner: pred?.winnerName,
                                tossWinner: pred?.tossWinnerName,
                                battingFirst: pred?.battingFirstName,
                                manOfMatch: pred?.manOfMatchName,
                                highestRunScorer: pred?.highestScorerName,
                                highestWicketTaker: pred?.highestWicketTakerName,
                              };
                              const predictedName = predictedNameMap?.[cat?.key];
                              const actualName = actualNameMap?.[cat?.key];
                              const hasActual = actualName && actualName !== '—';

                              if (!row?.hasPrediction || !pred) {
                                return (
                                  <td key={cat?.key} className="text-center px-3 py-3.5">
                                    <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                                  </td>
                                );
                              }

                              const hasPred = predictedName && predictedName !== '—';

                              return (
                                <td key={cat?.key} className="px-3 py-3.5">
                                  <div className="flex flex-col items-center gap-1">
                                    {/* Prediction row */}
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-gray-400 w-5 flex-shrink-0">P:</span>
                                      <span className={`text-xs font-medium truncate max-w-[100px] ${
                                        bd?.isCorrect
                                          ? 'text-emerald-700 dark:text-emerald-400'
                                          : hasPred && hasActual
                                          ? 'text-red-500 dark:text-red-400' :'text-gray-600 dark:text-gray-300'
                                      }`}>
                                        {hasPred ? predictedName : <span className="text-gray-400 italic">—</span>}
                                      </span>
                                    </div>
                                    {/* Result row */}
                                    {isResultPublished && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs text-gray-400 w-5 flex-shrink-0">R:</span>
                                        <span className="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[100px]">
                                          {hasActual ? actualName : <span className="text-gray-400 italic">—</span>}
                                        </span>
                                      </div>
                                    )}
                                    {/* Points badge */}
                                    {isResultPublished && bd && (
                                      bd?.isCorrect ? (
                                        <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold leading-none mt-0.5">
                                          +{bd?.points}
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-900/20 text-red-400 dark:text-red-500 text-xs leading-none mt-0.5">
                                          0
                                        </span>
                                      )
                                    )}
                                  </div>
                                </td>
                              );
                            })}

                            {/* Correct count */}
                            <td className="text-center px-3 py-3.5">
                              {row?.hasPrediction ? (
                                <span className={`text-sm font-semibold ${row?.correct > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}>
                                  {row?.correct}/{row?.totalPredictions}
                                </span>
                              ) : (
                                <span className="text-gray-300 dark:text-gray-600">—</span>
                              )}
                            </td>

                            {/* Total points */}
                            <td className="text-right px-5 py-3.5">
                              <span className={`text-base font-bold ${row?.total > 0 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400'}`}>
                                {row?.total}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {/* Footer totals row */}
                    <tfoot>
                      <tr className="bg-gray-50 dark:bg-gray-800/80 border-t-2 border-gray-200 dark:border-gray-600">
                        <td className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800/80 px-5 py-3">
                          <span className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Total</span>
                        </td>
                        {participantEnabledCategories?.map(cat => (
                          <td key={cat?.key} className="px-3 py-3" />
                        ))}
                        <td className="text-center px-3 py-3">
                          <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                            {participantTotals?.correct}/{participantTotals?.totalPredictions}
                          </span>
                        </td>
                        <td className="text-right px-5 py-3">
                          <span className="text-base font-bold text-primary-600 dark:text-primary-400">
                            {participantTotals?.total}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Legend */}
                <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <span className="font-medium text-gray-600 dark:text-gray-300">P:</span> Prediction
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="font-medium text-gray-600 dark:text-gray-300">R:</span> Result
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-emerald-100 dark:bg-emerald-900/40 inline-block" />
                    Correct (points awarded)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-sm bg-red-50 dark:bg-red-900/20 inline-block" />
                    Wrong (0 points)
                  </span>
                </div>
              </div>
            ) : null}
          </>
        )}
      </main>

      {/* Drill-down panel */}
      {drillDown && (
        <DrillDownPanel
          user={drillDown?.user}
          match={drillDown?.match}
          prediction={drillDown?.prediction}
          settings={drillDown?.settings}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
};

export default PointsVerification;
